/**
 * Tracking PRO — courier status badge.
 *
 * Renders live courier status on an ASN row. Three distinct states, because
 * they mean different things and must not look alike:
 *   - a real status  → coloured badge
 *   - a sync failure → amber warning (we asked and the carrier said no)
 *   - not registered → muted "Enable PRO" hint (we never asked)
 *
 * @link ASNTrackingPanel for the full timeline view.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
  Package,
  PackageCheck,
  Sparkles,
  Truck,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { ASNTrackingSummary, CourierTrackingStatus } from '../../../lib/api';

interface StatusStyle {
  icon: LucideIcon;
  className: string;
}

const STATUS_STYLES: Record<CourierTrackingStatus, StatusStyle> = {
  Pending: { icon: Clock, className: 'bg-gray-100 text-gray-700 border-gray-200' },
  InfoReceived: { icon: Package, className: 'bg-slate-100 text-slate-700 border-slate-200' },
  InTransit: { icon: Truck, className: 'bg-blue-100 text-blue-700 border-blue-200' },
  OutForDelivery: { icon: Truck, className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  AvailableForPickup: { icon: PackageCheck, className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  Delivered: { icon: CheckCircle2, className: 'bg-green-100 text-green-700 border-green-200' },
  AttemptFail: { icon: AlertTriangle, className: 'bg-orange-100 text-orange-700 border-orange-200' },
  Exception: { icon: XCircle, className: 'bg-red-100 text-red-700 border-red-200' },
  Expired: { icon: HelpCircle, className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const FALLBACK_STYLE: StatusStyle = {
  icon: HelpCircle,
  className: 'bg-gray-100 text-gray-600 border-gray-200',
};

/**
 * ASN statuses a courier feed can still say something useful about. Mirrors
 * REGISTERABLE_ASN_STATUSES in `purchasing/tracking_pro_tasks.py` — anything
 * else is closed out (delivered / received / cancelled) and will never be
 * registered, so promising tracking on those rows would be a lie.
 */
const LIVE_ASN_STATUSES = ['created', 'submitted', 'in_transit'];

interface CourierStatusBadgeProps {
  tracking?: ASNTrackingSummary | null;
  /** True when the ASN has a courier + tracking number to track at all. */
  trackable: boolean;
  /** Whether live tracking is switched on for the platform. */
  proEnabled?: boolean;
  /** The ASN's own status, used to tell live shipments from historic ones. */
  asnStatus?: string;
}

export function CourierStatusBadge({
  tracking, trackable, proEnabled, asnStatus,
}: CourierStatusBadgeProps) {
  // Nothing to track — own transport, or no usable tracking number.
  if (!trackable) {
    return <span className="text-muted-foreground">-</span>;
  }

  // Trackable but no tracking record — three genuinely different situations,
  // and showing an upsell or a promise for the wrong one reads as broken.
  if (!tracking) {
    // Already delivered / received / cancelled, or not yet dispatched. These
    // are never registered, so neither "Enable PRO" nor "Not tracked yet" is
    // true — there is simply nothing a courier can tell us.
    if (asnStatus && !LIVE_ASN_STATUSES.includes(asnStatus)) {
      return <span className="text-muted-foreground">-</span>;
    }
    if (!proEnabled) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500 border border-dashed border-gray-300"
          title="Live courier tracking is not enabled for this platform"
        >
          <Sparkles className="h-3 w-3 text-amber-500" />
          Enable PRO
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600 border border-slate-200"
        title="Queued for registration with the courier — updates within the hour, or open the ASN and hit Refresh"
      >
        <Clock className="h-3 w-3" />
        Not tracked yet
      </span>
    );
  }

  if (tracking.sync_state === 'error') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200"
        title={tracking.sync_error || 'Could not reach the courier for this shipment'}
      >
        <AlertTriangle className="h-3 w-3" />
        Unavailable
      </span>
    );
  }

  if (!tracking.status) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 border border-gray-200">
        <Clock className="h-3 w-3" />
        Awaiting scan
      </span>
    );
  }

  const style = STATUS_STYLES[tracking.status as CourierTrackingStatus] ?? FALLBACK_STYLE;
  const Icon = style.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${style.className}`}
      title={tracking.status_message || tracking.status_label}
    >
      <Icon className="h-3 w-3" />
      {tracking.status_label}
    </span>
  );
}

/**
 * True when an ASN carries enough detail for a courier to have anything to say.
 *
 * Mirrors the backend gate in `services/tracking_pro/config.py` — if these two
 * disagree, the UI offers a Refresh that the API is bound to reject.
 *
 * @param courier - ASN courier code
 * @param trackingNumber - ASN tracking number
 * @param courierOther - free-text name used when courier is 'other'
 */
export function isTrackableASN(
  courier?: string,
  trackingNumber?: string,
  courierOther?: string,
): boolean {
  const untrackable = ['own_transport', 'poeton_transport'];
  if (!courier || untrackable.includes(courier)) return false;

  // Live data carries placeholders ('0', '1', '1234'); the shortest genuine
  // carrier formats are 8+ characters.
  if ((trackingNumber || '').trim().length < 6) return false;

  // 'Other' covers both a real carrier we have no mapping for and our own van
  // with a driver's name typed in — the latter has nothing to track.
  if (courier === 'other') {
    const name = (courierOther || '').trim().toLowerCase();
    const ownTransportHints = ['poeton', 'driver', 'own transport', 'own vehicle', 'own van'];
    if (ownTransportHints.some((hint) => name.includes(hint))) return false;
  }

  return true;
}
